import { Combobox, Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  QUERY_BUILDER_CLOSE_BRACKET,
  QUERY_BUILDER_KEYWORD_ENTITY_KEY,
  QUERY_BUILDER_OPEN_BRACKET,
  QueryBuilderEntityConfigType,
  QueryBuilderEntityValueOptionType,
  QueryBuilderInputContextType,
  QueryBuilderOperatorType,
} from '../../types';

export type SuggestionsDropdownPropsType = {
  context: QueryBuilderInputContextType;
  search: string;
  filteredEntityOptions: QueryBuilderEntityConfigType[];
  filteredOperatorOptions: QueryBuilderOperatorType[];
  filteredValueOptions: QueryBuilderEntityValueOptionType[];
  isLoadingValues: boolean;
  canOpenBracketOption: boolean;
  canCloseBracketOption: boolean;
  pendingEntityKey: string | null;
};

export const SuggestionsDropdown: React.FC<SuggestionsDropdownPropsType> = ({
  context,
  search,
  filteredEntityOptions,
  filteredOperatorOptions,
  filteredValueOptions,
  isLoadingValues,
  canOpenBracketOption,
  canCloseBracketOption,
  pendingEntityKey,
}) => {
  const { t } = useTranslation();

  const entitySuggestions = filteredEntityOptions.map((entity) => (
    <Combobox.Option key={entity.key} value={entity.key}>
      {entity.label}
    </Combobox.Option>
  ));

  const operatorSuggestions = filteredOperatorOptions.map((operator) => (
    <Combobox.Option key={operator} value={operator}>
      {operator}
    </Combobox.Option>
  ));

  const keywordWithSearchSuggestion = (
    <Combobox.Option key="keyword" value="keyword">
      {t('QUERY_BUILDER.KEYWORD')}: {search}
    </Combobox.Option>
  );

  const valueSuggestions = filteredValueOptions.map((option) => (
    <Combobox.Option key={option.label} value={option.label}>
      {option.label}
    </Combobox.Option>
  ));

  const isSearchForOpenBracket =
    search.trim() === QUERY_BUILDER_OPEN_BRACKET || search.trim() === '';
  const isSearchForCloseBracket =
    search.trim() === QUERY_BUILDER_CLOSE_BRACKET || search.trim() === '';

  const currentEntityIsKeyword =
    context === 'expectValue' &&
    pendingEntityKey === QUERY_BUILDER_KEYWORD_ENTITY_KEY;

  return (
    <Combobox.Dropdown>
      <Combobox.Options>
        {context === 'expectOperatorOrClose' ? operatorSuggestions : null}
        {context === 'expectOperatorOrClose' &&
        canCloseBracketOption &&
        isSearchForCloseBracket ? (
          <Combobox.Option value={QUERY_BUILDER_CLOSE_BRACKET}>
            {QUERY_BUILDER_CLOSE_BRACKET}
          </Combobox.Option>
        ) : null}
        {context === 'expectOperatorOrClose' &&
        operatorSuggestions.length === 0 &&
        (!canCloseBracketOption || !isSearchForCloseBracket) ? (
          <Combobox.Empty>{t('QUERY_BUILDER.NO_RESULTS')}</Combobox.Empty>
        ) : null}

        {context === 'expectOperand' &&
        canOpenBracketOption &&
        isSearchForOpenBracket ? (
          <Combobox.Option value={QUERY_BUILDER_OPEN_BRACKET}>
            {QUERY_BUILDER_OPEN_BRACKET}
          </Combobox.Option>
        ) : null}
        {context === 'expectOperand' && filteredEntityOptions.length > 0
          ? entitySuggestions
          : null}
        {context === 'expectOperand' &&
        filteredEntityOptions.length === 0 &&
        search.trim().length > 0
          ? keywordWithSearchSuggestion
          : null}
        {currentEntityIsKeyword ? keywordWithSearchSuggestion : null}

        {context === 'expectValue' && isLoadingValues ? (
          <Combobox.Empty>
            <Loader size="xs" />
          </Combobox.Empty>
        ) : null}
        {context === 'expectValue' && !isLoadingValues
          ? valueSuggestions
          : null}

        {context === 'expectValue' &&
        !isLoadingValues &&
        filteredValueOptions.length === 0 &&
        search.trim().length > 0 &&
        !currentEntityIsKeyword ? (
          <Combobox.Empty>{t('QUERY_BUILDER.NO_RESULTS')}</Combobox.Empty>
        ) : null}
      </Combobox.Options>
    </Combobox.Dropdown>
  );
};
